import React from "react";
import classnames from "classnames";
import Link from "next/link";

import { Action, Category } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";
import useFilters from "@/hooks/useFilters";

import IconSvg from "@/components/IconSvg";

type Props = {
  asLink?: boolean;
  linkToDeals?: boolean;
  className?: string;
};

const PhenotypeCarousel: React.FC<Props> = ({
  asLink,
  linkToDeals,
  className,
}) => {
  const { selectFilterValue, addFilter } = useFilters();
  const selectedPhenotype = selectFilterValue("strain_category");
  const { publishEvent } = useEventTracker();

  const handlePhenotypeClick = (e: React.MouseEvent<HTMLElement>) => {
    const phenotype = e.currentTarget?.dataset?.phenotype || "";
    addFilter({
      strain_category: phenotype,
    });
    publishEvent({
      action: Action.click,
      category: Category.strainTypeFilter,
      label: phenotype?.toLowerCase(),
    });
  };

  const wrapperClass = "flex-shrink-0 flex-grow-0 mr-md";
  const coinClass = "flex flex-col items-center";

  const coinContent = (phenotype: string) => (
    <>
      <div className="rounded-full bg-white shadow-low w-[80px] h-[80px] flex items-center justify-center">
        <IconSvg
          filePath={`phenotypes/${phenotype.toLowerCase()}.svg`}
          width="60"
          height="60"
        />
      </div>
      <div
        className={classnames("text-sm font-bold mt-lg", {
          "text-green": !selectedPhenotype || phenotype === selectedPhenotype,
        })}
      >
        {phenotype}
      </div>
    </>
  );

  return (
    <>
      <div
        className={classnames(
          "flex justify-start flex-nowrap overflow-auto",
          className,
        )}
      >
        {["Indica", "Sativa", "Hybrid"].map((phenotype) =>
          asLink ? (
            <Link
              data-testid={phenotype.toLowerCase()}
              key={phenotype}
              href={`/search/shop/phenotype?strain_category=${phenotype}${
                linkToDeals ? "&deal_active=true" : ""
              }`}
              onClick={() => {
                publishEvent({
                  action: Action.click,
                  category: Category.strainTypeFilter,
                  label: phenotype.toLowerCase(),
                });
              }}
              className={wrapperClass}
            >
              <div className={coinClass}>{coinContent(phenotype)}</div>
            </Link>
          ) : (
            <button
              data-testid={phenotype.toLowerCase()}
              key={phenotype}
              onClick={handlePhenotypeClick}
              data-phenotype={phenotype}
              className={wrapperClass}
            >
              <div className={coinClass}>{coinContent(phenotype)}</div>
            </button>
          ),
        )}
      </div>
    </>
  );
};

export default PhenotypeCarousel;
